/**
 * PersonForm — the add / edit form of a saved person's chart («Мои карты»).
 *
 * Reuses the onboarding birth widgets (PrettyDateInput, TimeOfDayPicker, PlaceAutocomplete).
 * The form owns its field state, starting from `initial`; the screen decides what to send:
 * `createBody` for a new chart, `patchBody` (changed fields only) for an edit.
 * Only one PlaceAutocomplete lives on the page.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlaceAutocomplete, type SelectedPlace } from '../onboarding/PlaceAutocomplete';
import { PrettyDateInput } from '../onboarding/PrettyDateInput';
import { TimeOfDayPicker, type TimeOfDay } from '../onboarding/TimeOfDayPicker';
import type { ChartCreateBody, ChartItem, ChartPatchBody, Relation, Sex } from '../../hooks/useCharts';
import {
  PRECISION_TO_TIME_OF_DAY,
  TIME_OF_DAY_TO_PRECISION,
  deriveTimeOfDayFromExact,
} from '../../lib/birth-time';
import { relationKey, sexKey } from '../../lib/relation';
import styles from './PersonForm.module.css';

export interface PersonFormValue {
  name: string;
  relation: Relation | null;
  sex: Sex | null;
  /** YYYY-MM-DD */
  date: string;
  timeUnknown: boolean;
  /** HH:MM, '' when not entered */
  exactTime: string;
  timeOfDay: TimeOfDay | null;
  place: SelectedPlace | null;
}

export interface PersonFormProps {
  initial: PersonFormValue;
  submitLabel: string;
  busy: boolean;
  /** `ApiError.code` of the last failed save, or null. */
  errorCode: string | null;
  onSubmit: (v: PersonFormValue) => void;
  onCancel: () => void;
}

const RELATIONS: Relation[] = ['SON', 'DAUGHTER', 'MOTHER', 'FATHER', 'PARTNER', 'FRIEND', 'COLLEAGUE', 'OTHER'];
const NAME_MAX = 40;
/** The server's timeExact rule. */
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Server error code → key under `me:charts.form.errors`. */
const ERROR_KEYS: Record<string, string> = {
  chart_limit: 'chartLimit',
  rate_limited: 'rateLimited',
  name_required: 'nameRequired',
  name_too_long: 'nameTooLong',
  date_in_future: 'dateInFuture',
  invalid_date: 'invalidDate',
  invalid_date_format: 'invalidDate',
  invalid_time_format: 'invalidTime',
  timeExact_required_when_precision_EXACT: 'invalidTime',
  timeExact_requires_precision_EXACT: 'invalidTime',
  chart_not_found: 'chartNotFound',
};

export function emptyPersonForm(): PersonFormValue {
  return { name: '', relation: null, sex: null, date: '', timeUnknown: false, exactTime: '', timeOfDay: null, place: null };
}

export function formFromChart(c: ChartItem): PersonFormValue {
  const exact = c.timePrecision === 'EXACT';
  const exactTime = exact ? (c.timeExact ?? '') : '';
  return {
    name: c.name,
    relation: c.relation,
    sex: c.sex,
    date: c.date,
    timeUnknown: !exact,
    exactTime,
    timeOfDay: exact ? deriveTimeOfDayFromExact(exactTime) : PRECISION_TO_TIME_OF_DAY[c.timePrecision as Exclude<ChartItem['timePrecision'], 'EXACT'>],
    place: {
      placeId: 'prefill', placeName: c.placeName, state: null, country: null, countryCode: null,
      lat: c.lat, lng: c.lng, tz: c.tz,
    },
  };
}

/** The server collapses whitespace runs to one space and trims; do the same so «unchanged» compares right. */
const cleanName = (n: string): string => n.replace(/[\s\p{Cc}]+/gu, ' ').trim();

export function createBody(v: PersonFormValue): ChartCreateBody {
  const place = v.place as SelectedPlace;
  return {
    kind: 'PERSON',
    name: cleanName(v.name),
    relation: v.relation as Relation,
    ...(v.sex ? { sex: v.sex } : {}),
    date: v.date,
    ...(v.timeUnknown
      ? { timePrecision: TIME_OF_DAY_TO_PRECISION[v.timeOfDay as TimeOfDay] }
      : { timePrecision: 'EXACT' as const, timeExact: v.exactTime }),
    placeName: place.placeName,
    lat: place.lat,
    lng: place.lng,
    tz: place.tz,
  };
}

/**
 * Only changed fields; {} when nothing changed. A place counts as changed only when it was picked anew
 * (placeId !== 'prefill') and differs from the stored one (the same place picked again is not a change).
 */
export function patchBody(before: ChartItem, v: PersonFormValue): ChartPatchBody {
  const body: ChartPatchBody = {};
  const name = cleanName(v.name);
  if (name !== before.name) body.name = name;
  if (v.relation && v.relation !== before.relation) body.relation = v.relation;
  if (v.sex !== before.sex) body.sex = v.sex;
  if (v.date !== before.date) body.date = v.date;

  if (v.timeUnknown) {
    const precision = TIME_OF_DAY_TO_PRECISION[v.timeOfDay as TimeOfDay];
    if (precision !== before.timePrecision) {
      body.timePrecision = precision;
      if (before.timePrecision === 'EXACT') body.timeExact = null;
    }
  } else if (before.timePrecision !== 'EXACT' || v.exactTime !== (before.timeExact ?? '')) {
    body.timePrecision = 'EXACT';
    body.timeExact = v.exactTime;
  }

  const p = v.place;
  const samePlace = p !== null
    && p.placeName === before.placeName && p.lat === before.lat && p.lng === before.lng && p.tz === before.tz;
  if (p && p.placeId !== 'prefill' && !samePlace) {
    body.placeName = p.placeName;
    body.lat = p.lat;
    body.lng = p.lng;
    body.tz = p.tz;
  }
  return body;
}

/** The sex a relation implies (son/father → male, daughter/mother → female), or null. */
function impliedSexOf(r: Relation | null): Sex | null {
  const k = sexKey(null, r);
  return k === 'male' ? 'MALE' : k === 'female' ? 'FEMALE' : null;
}

export function PersonForm({ initial, submitLabel, busy, errorCode, onSubmit, onCancel }: PersonFormProps): JSX.Element {
  const { t } = useTranslation('me');
  const { t: tc } = useTranslation('common');
  const [v, setV] = useState<PersonFormValue>(initial);
  // Whether the sex was chosen by the user. While it was not, choosing another relation re-derives it:
  // son/father → male, daughter/mother → female, others → empty. In edit mode a stored sex counts as
  // chosen only when it differs from what the stored relation implies (the form usually set it itself).
  const [sexTouched, setSexTouched] = useState<boolean>(
    initial.sex !== null && initial.sex !== impliedSexOf(initial.relation),
  );
  const patch = (p: Partial<PersonFormValue>): void => setV((cur) => ({ ...cur, ...p }));

  const nameOk = cleanName(v.name) !== '';
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(v.date);
  const timeOk = v.timeUnknown ? v.timeOfDay !== null : TIME_RE.test(v.exactTime);
  const canSubmit = nameOk && v.relation !== null && dateOk && timeOk && v.place !== null && !busy;

  const chooseRelation = (r: Relation): void => {
    setV((cur) => ({ ...cur, relation: r, sex: sexTouched ? cur.sex : impliedSexOf(r) }));
  };
  const toggleSex = (s: Sex): void => {
    setSexTouched(true);
    patch({ sex: v.sex === s ? null : s });
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (canSubmit) onSubmit(v);
  };

  const errorText = errorCode ? t(`charts.form.errors.${ERROR_KEYS[errorCode] ?? 'generic'}`) : null;

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.field}>
        <span className={styles.label}>{t('charts.form.name')}</span>
        <input
          type="text"
          className={styles.input}
          value={v.name}
          maxLength={NAME_MAX}
          onChange={(e) => patch({ name: e.target.value })}
          aria-label={t('charts.form.name')}
          placeholder={t('charts.form.namePlaceholder')}
          autoComplete="off"
        />
      </div>

      <fieldset className={styles.field}>
        <legend className={styles.label}>{t('charts.form.relation')}</legend>
        <div className={styles.chips}>
          {RELATIONS.map((r) => (
            <button
              key={r}
              type="button"
              className={`${styles.chip}${v.relation === r ? ` ${styles.chipActive}` : ''}`}
              aria-pressed={v.relation === r}
              onClick={() => chooseRelation(r)}
            >
              {t(`charts.relation.${relationKey(r)}`)}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.field}>
        <legend className={styles.label}>{t('charts.form.sex')}</legend>
        <div className={styles.segmented}>
          {(['MALE', 'FEMALE'] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={`${styles.segmentBtn}${v.sex === s ? ` ${styles.segmentBtnActive}` : ''}`}
              aria-pressed={v.sex === s}
              onClick={() => toggleSex(s)}
            >
              {t(s === 'MALE' ? 'charts.form.male' : 'charts.form.female')}
            </button>
          ))}
        </div>
      </fieldset>

      <div className={styles.field}>
        <span className={styles.label}>{t('charts.form.birthDate')}</span>
        <PrettyDateInput value={v.date} onChange={(date) => patch({ date })} ariaLabel={t('charts.form.birthDate')} />
      </div>

      <fieldset className={styles.field}>
        <legend className={styles.label}>{t('charts.form.birthTime')}</legend>
        <TimeOfDayPicker
          value={v.timeOfDay}
          exactTime={v.exactTime || null}
          unknownMode={v.timeUnknown}
          onChange={(timeOfDay) => patch({ timeOfDay })}
          onExactTimeChange={(exactTime, timeOfDay) => patch({ exactTime, timeOfDay })}
          onUnknownToggle={(unknown) =>
            patch(unknown ? { timeUnknown: true, exactTime: '' } : { timeUnknown: false, timeOfDay: null })
          }
        />
      </fieldset>

      <div className={styles.field}>
        <span className={styles.label}>{t('charts.form.birthPlace')}</span>
        <PlaceAutocomplete
          value={v.place}
          onChange={(place) => patch({ place })}
          placeholder={t('charts.form.birthPlace')}
          ariaLabel={t('charts.form.birthPlace')}
        />
      </div>

      {errorText && (
        <p className={styles.error} role="alert">
          {errorText}
        </p>
      )}

      <button type="submit" className={styles.cta} disabled={!canSubmit} aria-disabled={!canSubmit}>
        {busy ? t('charts.form.saving') : submitLabel}
      </button>
      <button type="button" className={styles.cancel} onClick={onCancel}>
        {tc('actions.cancel')}
      </button>
    </form>
  );
}
